import React, { useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

export const LoginView = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    try {
      const res = await axios.post("/api/login", { email, password }, { withCredentials: true });
     if (res.data.success) {
  // 🔐 store password TEMPORARILY (memory only)
  window.__E2EE_PASSWORD__ = password;

  await onLoginSuccess?.();

  navigate("/", { replace: true });
  return;
}
      if (!res.data.success && res.data.message === "Invalid credentials") { onLoginSuccess?.("account"); return; }
      setErrorMessage(res.data.message || "Login failed");
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Login error");
    } finally {
      setLoading(false);
    }
  };

  const inputBase = `w-full rounded-xl border bg-white dark:bg-neutral-800 px-4 py-3.5 sm:py-3 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 transition-all duration-200 outline-none`;
  const inputFocused = "border-green-500 ring-2 ring-green-500/20 shadow-sm";
  const inputResting = "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600";

  return (
       <div
      className="h-screen w-full flex overflow-hidden bg-neutral-50 dark:bg-neutral-900"
      style={{ height: "100dvh" }}
    >
      {/* LEFT PANEL — desktop/tablet only */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[40%] flex-col relative overflow-hidden bg-gradient-to-br from-green-700 via-green-600 to-emerald-500">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-white/5 blur-2xl" />
        <div className="absolute top-1/3 -right-16 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
        <div className="absolute -bottom-20 left-1/4 w-72 h-72 rounded-full bg-emerald-400/20 blur-2xl" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative z-10 flex flex-col justify-between h-full p-10">
       <div className="flex justify-center items-center gap-3 mb-4">
  <div className="w-10 h-10 rounded-full overflow-hidden bg-white shadow-lg">
    <img
      src="/logo192.png"
      alt="Metisel"
      className="w-full h-full object-cover"
    />
  </div>

  <h1 className="text-3xl font-bold text-white tracking-tight select-none">
    Metisel
  </h1>
</div>
          <div className="space-y-5">
            <h2 className="text-white text-4xl font-bold leading-tight tracking-tight">Connect with<br />people that<br />matter.</h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs">Share posts, follow friends, and stay in the loop — all in one place.</p>
            <div className="flex items-center gap-3 pt-2">
              <div className="flex -space-x-2">
                {["bg-yellow-400", "bg-pink-400", "bg-blue-400", "bg-purple-400"].map((c, i) => (
                  <div key={i} className={`w-8 h-8 rounded-full border-2 border-green-600 ${c} flex items-center justify-center text-xs font-bold text-white`}>{["J","A","M","S"][i]}</div>
                ))}
              </div>
              <p className="text-white/70 text-xs">Join thousands already on Metisel</p>
            </div>
          </div>
          <p className="text-white/40 text-xs">© {new Date().getFullYear()} Metisel. All rights reserved.</p>
        </div>
      </div>

      {/* RIGHT PANEL — form */}
      <div
        className="flex-1 flex items-center justify-center overflow-y-auto"
        style={{
          padding: "env(safe-area-inset-top, 24px) 16px env(safe-area-inset-bottom, 24px)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div className="w-full max-w-sm py-8 sm:py-10">
{/* Mobile logo only */}
<div className="flex lg:hidden justify-center mb-8">
  <div className="w-12 h-12 rounded-full overflow-hidden bg-white shadow-lg shadow-green-500/30">
    <img
      src="/logo192.png"
      alt="Metisel"
      className="w-full h-full object-cover"
    />
  </div>
</div>
          {/* Header — centered */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white tracking-tight">Hey! It's great to see you back.</h1>
            <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">Sign in to your account to continue</p>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-5 sm:p-6 space-y-4">
            {errorMessage && (
              <div className="flex items-start gap-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 px-4 py-3">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="login-email" className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400">Email</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                </span>
                <input
                id="login-email" name="email" type="email" autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@gmail.com"
                  onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)} required
                  className={`${inputBase} pl-10 ${focusedField === "email" ? inputFocused : inputResting}`}
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="login-password" className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400">Password</label>
                <button type="button" className="text-xs text-green-600 dark:text-green-400 font-medium hover:underline touch-manipulation" onClick={() => onLoginSuccess?.("forgot")}>
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                </span>
                <input
                  id="login-password" type={showPassword ? "text" : "password"} autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password"
                  onFocus={() => setFocusedField("password")} onBlur={() => setFocusedField(null)} required
                  className={`${inputBase} pl-10 pr-11 ${focusedField === "password" ? inputFocused : inputResting}`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors p-1 touch-manipulation">
                  {showPassword
                    ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                    : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  }
                </button>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-1">
              <button
                type="submit" disabled={loading}
                className={`w-full py-3.5 sm:py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 tracking-wide touch-manipulation ${loading ? "bg-green-400 cursor-not-allowed opacity-70" : "bg-green-500 hover:bg-green-600 active:scale-[0.98] shadow-md shadow-green-500/25 hover:shadow-lg hover:shadow-green-500/30"}`}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                    Signing in…
                  </span>
                ) : "Sign In"}
              </button>
            </div>
            </form>

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
              <span className="text-xs text-neutral-400">or</span>
              <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
            </div>

            <button
              type="button" onClick={() => onLoginSuccess?.("account")}
              className="w-full py-3.5 sm:py-3 rounded-xl text-sm font-semibold border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 hover:border-neutral-300 dark:hover:border-neutral-600 active:scale-[0.98] transition-all duration-200 touch-manipulation"
            >
              Create an account
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default function Login(props) {
  return <LoginView {...props} />;
}